import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  CloudService,
  GitHubDestination,
  ItemChannelMode,
  ItemPolicy,
  QuotaInfo,
  ResolvedChannelState,
  ResolvedPolicy,
  ServiceCardData,
  ServiceId,
  SlackDestination,
  TeamsDestination,
  EmailDestination,
  JiraDestination,
} from '../../core/services/cloud.service';
import { MemberWithUser } from '../../core/services/org.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
  selector: 'app-item-settings-drawer',
  templateUrl: './item-settings-drawer.component.html',
})
export class ItemSettingsDrawerComponent implements OnChanges {
  @Input() service: ServiceCardData | null = null;
  @Input() itemId: string | null = null;
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();

  draft: ItemPolicy | null = null;
  resolved: ResolvedPolicy | null = null;
  saveState: 'idle' | 'saving' = 'idle';
  showDiscardConfirm = false;
  submitAttempted = false;

  isMonitored = false;
  monitoringState: 'idle' | 'saving' = 'idle';
  quotaInfo: QuotaInfo | null = null;

  private initialDraftStr = '';

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        this.resetDraft();
      } else {
        this.showDiscardConfirm = false;
        this.submitAttempted = false;
      }
    }
  }

  private resetDraft(): void {
    if (!this.service || !this.itemId) return;
    const sid    = this.service.id as ServiceId;
    const stored = this.cloud.getItemPolicy(sid, this.itemId);
    this.draft   = stored
      ? (JSON.parse(JSON.stringify(stored)) as ItemPolicy)
      : makeDefaultDraft();
    this.saveState          = 'idle';
    this.showDiscardConfirm = false;
    this.submitAttempted    = false;
    this.initialDraftStr    = JSON.stringify(this.draft);
    this.isMonitored        = this.cloud.isMonitored(sid, this.itemId);
    this.quotaInfo          = this.cloud.getQuotaInfo();
    this.monitoringState    = 'idle';
    this.refreshResolved();
  }

  /** Re-computes the effective policy from current service config + draft item policy. */
  private refreshResolved(): void {
    if (!this.service || !this.itemId) return;
    // Temporarily write draft into service so resolution sees it (or use service-level resolution directly)
    // For display purposes, use existing service policy + current draft item policy
    this.resolved = this.cloud.resolveEffectivePolicy(this.service.id as ServiceId, this.itemId);
  }

  get isDirty(): boolean {
    return !!this.draft && JSON.stringify(this.draft) !== this.initialDraftStr;
  }

  get orgMembers(): MemberWithUser[] { return this.cloud.getOrgMembers(); }

  // ── Effective-state summaries for inherit-mode display ───────────────────────

  slackEffectiveSummary(): string {
    if (!this.resolved) return '';
    return describeChannelState(this.resolved.notifications.slack, 'Slack webhook');
  }

  teamsEffectiveSummary(): string {
    if (!this.resolved) return '';
    return describeChannelState(this.resolved.notifications.teams, 'Teams webhook');
  }

  emailEffectiveSummary(): string {
    if (!this.resolved) return '';
    const s = this.resolved.notifications.email;
    if (!s.enabled) return 'Disabled at service level';
    if (s.isConfigMissing) return 'Enabled — no destination configured';
    const ids    = (s.config as EmailDestination).recipientIds;
    const names  = ids.map(id => {
      const m = this.orgMembers.find(x => x.userId === id);
      return m ? (`${m.givenName} ${m.familyName}`.trim() || m.email) : id;
    });
    const prefix = s.source === 'org' ? 'Organization default' : 'Service';
    return `${prefix}: ${ids.length} recipient${ids.length !== 1 ? 's' : ''} (${names.join(', ')})`;
  }

  githubEffectiveSummary(): string {
    if (!this.resolved) return '';
    const s = this.resolved.ticketing.github;
    if (!s.enabled) return 'Disabled at service level';
    if (s.isConfigMissing) return 'Enabled — no destination configured';
    return `${s.source === 'org' ? 'Organization default' : 'Service'}: ${(s.config as GitHubDestination).repository}`;
  }

  jiraEffectiveSummary(): string {
    if (!this.resolved) return '';
    const s = this.resolved.ticketing.jira;
    if (!s.enabled) return 'Disabled at service level';
    if (s.isConfigMissing) return 'Enabled — no destination configured';
    return `${s.source === 'org' ? 'Organization default' : 'Service'}: project ${(s.config as JiraDestination).projectKey}`;
  }

  // ── Incomplete config warnings (enabled-inherited but no destination exists) ──

  get slackInheritedMissing(): boolean {
    return this.draft?.notifications.slack.mode === 'enabled-inherited' &&
      (this.resolved?.notifications.slack.isConfigMissing ?? false);
  }

  get teamsInheritedMissing(): boolean {
    return this.draft?.notifications.teams.mode === 'enabled-inherited' &&
      (this.resolved?.notifications.teams.isConfigMissing ?? false);
  }

  get emailInheritedMissing(): boolean {
    return this.draft?.notifications.email.mode === 'enabled-inherited' &&
      (this.resolved?.notifications.email.isConfigMissing ?? false);
  }

  get githubInheritedMissing(): boolean {
    return this.draft?.ticketing.github.mode === 'enabled-inherited' &&
      (this.resolved?.ticketing.github.isConfigMissing ?? false);
  }

  get jiraInheritedMissing(): boolean {
    return this.draft?.ticketing.jira.mode === 'enabled-inherited' &&
      (this.resolved?.ticketing.jira.isConfigMissing ?? false);
  }

  // ── Validation error getters (only active after first save attempt) ──────────

  get slackUrlError(): boolean {
    return this.submitAttempted &&
      this.draft?.notifications.slack.mode === 'enabled-custom' &&
      !isValidUrl(this.draft?.notifications.slack.overrideConfig?.webhookUrl ?? '');
  }

  get teamsUrlError(): boolean {
    return this.submitAttempted &&
      this.draft?.notifications.teams.mode === 'enabled-custom' &&
      !isValidUrl(this.draft?.notifications.teams.overrideConfig?.webhookUrl ?? '');
  }

  get emailError(): boolean {
    return this.submitAttempted &&
      this.draft?.notifications.email.mode === 'enabled-custom' &&
      (this.draft?.notifications.email.overrideConfig?.recipientIds?.length ?? 0) === 0;
  }

  get githubError(): boolean {
    return this.submitAttempted &&
      this.draft?.ticketing.github.mode === 'enabled-custom' &&
      !(this.draft?.ticketing.github.overrideConfig?.repository?.includes('/') ?? false);
  }

  get jiraError(): boolean {
    return this.submitAttempted &&
      this.draft?.ticketing.jira.mode === 'enabled-custom' &&
      !(this.draft?.ticketing.jira.overrideConfig?.projectKey?.trim() ?? '');
  }

  // ── Mode helpers for template ─────────────────────────────────────────────────

  setNotifMode(provider: 'slack' | 'teams' | 'email', mode: ItemChannelMode): void {
    if (!this.draft) return;
    this.draft.notifications[provider].mode = mode;
  }

  setTicketMode(provider: 'github' | 'jira', mode: ItemChannelMode): void {
    if (!this.draft) return;
    this.draft.ticketing[provider].mode = mode;
  }

  revertToService(): void {
    if (!this.draft) return;
    (['slack', 'teams', 'email'] as const).forEach(k => { this.draft!.notifications[k].mode = 'service'; });
    (['github', 'jira']          as const).forEach(k => { this.draft!.ticketing[k].mode    = 'service'; });
  }

  get hasAnyOverride(): boolean {
    if (!this.draft) return false;
    return (['slack', 'teams', 'email'] as const).some(k => this.draft!.notifications[k].mode !== 'service') ||
           (['github', 'jira']          as const).some(k => this.draft!.ticketing[k].mode    !== 'service');
  }

  // ── Actions ──────────────────────────────────────────────────────────────────

  requestClose(): void {
    if (this.isDirty) {
      this.showDiscardConfirm = true;
    } else {
      this.closed.emit();
    }
  }

  confirmDiscard(): void {
    this.showDiscardConfirm = false;
    this.closed.emit();
  }

  cancelDiscard(): void {
    this.showDiscardConfirm = false;
  }

  toggleMonitoring(): void {
    if (!this.service || !this.itemId || this.monitoringState === 'saving') return;
    const newValue = !this.isMonitored;
    const sid      = this.service.id as ServiceId;
    this.monitoringState = 'saving';
    this.cloud.toggleMonitoring(sid, this.itemId, newValue, undefined, { name: this.itemId }).subscribe({
      next: res => {
        this.isMonitored     = res.isMonitored;
        this.quotaInfo       = res.quota;
        this.monitoringState = 'idle';
        this.toast.show(newValue ? 'Monitoring enabled' : 'Monitoring disabled');
      },
      error: (err: { status: number; error?: { message?: string } }) => {
        this.monitoringState = 'idle';
        if (err.status === 403) {
          this.toast.show(err.error?.message ?? this.cloud.getQuotaExceededMessage(), 'error');
        } else {
          this.toast.show('Failed to update monitoring', 'error');
        }
      },
    });
  }

  save(): void {
    this.submitAttempted = true;
    if (!this.isFormValid() || !this.service || !this.itemId || !this.draft) return;
    this.saveState = 'saving';
    this.cloud.saveItemPolicy(this.service.id as ServiceId, this.itemId, this.draft).subscribe({
      next: () => {
        this.saveState = 'idle';
        const label = this.itemId!.length > 30
          ? this.itemId!.slice(0, 30) + '…'
          : this.itemId!;
        this.toast.show(`${label} settings updated`);
        this.closed.emit();
      },
      error: () => {
        this.saveState = 'idle';
        this.toast.show('Failed to save settings', 'error');
      },
    });
  }

  private isFormValid(): boolean {
    if (!this.draft) return false;
    const n = this.draft.notifications;
    const t = this.draft.ticketing;
    if (n.slack.mode  === 'enabled-custom' && !isValidUrl(n.slack.overrideConfig?.webhookUrl  ?? '')) return false;
    if (n.teams.mode  === 'enabled-custom' && !isValidUrl(n.teams.overrideConfig?.webhookUrl  ?? '')) return false;
    if (n.email.mode  === 'enabled-custom' && (n.email.overrideConfig?.recipientIds?.length ?? 0) === 0) return false;
    if (t.github.mode === 'enabled-custom' && !(t.github.overrideConfig?.repository?.includes('/') ?? false)) return false;
    if (t.jira.mode   === 'enabled-custom' && !(t.jira.overrideConfig?.projectKey?.trim()    ?? '')) return false;
    return true;
  }
}

// ── Module-private helpers ────────────────────────────────────────────────────

function makeDefaultDraft(): ItemPolicy {
  return {
    notifications: {
      slack: { mode: 'service' },
      teams: { mode: 'service' },
      email: { mode: 'service' },
    },
    ticketing: {
      github: { mode: 'service' },
      jira:   { mode: 'service' },
    },
  };
}

function isValidUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function describeChannelState(
  s: ResolvedChannelState<SlackDestination | TeamsDestination>,
  label: string,
): string {
  if (!s.enabled) return 'Disabled at service level';
  if (s.isConfigMissing) return 'Enabled — no destination configured';
  const dest = s.config as SlackDestination | TeamsDestination;
  const url  = (dest as SlackDestination).webhookUrl ?? '';
  try {
    const u = new URL(url);
    return `${s.source === 'org' ? 'Organization default' : 'Service'}: ${u.hostname}/…${url.slice(-6)}`;
  } catch {
    return `${s.source === 'org' ? 'Organization default' : 'Service'} ${label}`;
  }
}
